import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Images, Loader2, Upload, X } from 'lucide-react'

import type { ProductionAsset } from '@/api/studio-production'
import { patchSceneStoryboard } from '@/api/studio-production'
import { AssetMultiPickerModal } from '@/features/studio/modals/AssetMultiPickerModal'
import { formatResolvedApiError, resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { assetMediaUrl, resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import {
  collectSceneReferenceUrls,
  linkAssetsToSceneReference,
  listSceneReferenceAssets,
  unlinkSceneReferenceAsset,
  uploadSceneReferenceAsset,
} from '@/features/studio/lib/scene-reference-assets'
import { Button } from '@/shared/components/ui/button'
import { appIconBtnClassName } from '@/shared/lib/app-button'

export function SceneReferenceAssets({
  productionId,
  scriptId,
  episodeNo,
  sceneNo,
  scopeKey,
  storyboardUrl,
  canEdit,
  compact,
  onStoryboardChange,
  onPreview,
}: {
  productionId: string
  scriptId: number
  episodeNo: number
  sceneNo: number
  scopeKey: string
  storyboardUrl?: string
  canEdit: boolean
  compact?: boolean
  onStoryboardChange?: (url: string) => void
  onPreview?: (urls: string[], index: number, title: string) => void
}) {
  const { t } = useTranslation()
  const fileRef = useRef<HTMLInputElement>(null)
  const [assets, setAssets] = useState<ProductionAsset[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const items = await listSceneReferenceAssets(productionId, scopeKey)
      setAssets(items ?? [])
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.sceneRefsLoadFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId, scopeKey])

  useEffect(() => {
    void reload()
  }, [reload])

  const displayUrls = useMemo(
    () => collectSceneReferenceUrls(assets, storyboardUrl),
    [assets, storyboardUrl],
  )

  async function applyCoverFromAssets(nextAssets: ProductionAsset[]) {
    const cover = collectSceneReferenceUrls(nextAssets)[0] ?? ''
    if (cover !== (storyboardUrl?.trim() ?? '')) {
      await patchSceneStoryboard(productionId, scriptId, episodeNo, sceneNo, cover)
    }
    onStoryboardChange?.(cover)
  }

  async function handleUpload(file: File) {
    setBusy(true)
    setError(null)
    try {
      await uploadSceneReferenceAsset(productionId, scopeKey, sceneNo, file)
      const items = await listSceneReferenceAssets(productionId, scopeKey)
      setAssets(items ?? [])
      await applyCoverFromAssets(items)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.sceneStoryboardUploadFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function handlePick(urls: string[], catalog?: ProductionAsset[]) {
    setBusy(true)
    setError(null)
    try {
      await linkAssetsToSceneReference(productionId, scopeKey, urls, catalog)
      const items = await listSceneReferenceAssets(productionId, scopeKey)
      setAssets(items ?? [])
      await applyCoverFromAssets(items)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.sceneRefsLinkFailed'))
    } finally {
      setBusy(false)
      setPickerOpen(false)
    }
  }

  async function handleRemove(asset: ProductionAsset | null, url: string) {
    setBusy(true)
    setError(null)
    try {
      if (asset) {
        await unlinkSceneReferenceAsset(productionId, scopeKey, asset)
      } else if (url.trim() && url.trim() === storyboardUrl?.trim()) {
        await patchSceneStoryboard(productionId, scriptId, episodeNo, sceneNo, '')
      }
      const items = await listSceneReferenceAssets(productionId, scopeKey)
      setAssets(items ?? [])
      await applyCoverFromAssets(items)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.sceneRefsRemoveFailed'))
    } finally {
      setBusy(false)
    }
  }

  const sceneCode = `S${String(sceneNo).padStart(2, '0')}`
  const tileClass = compact ? 'h-14 w-14' : 'h-16 w-16'

  return (
    <div className="space-y-2">
      {error ? (
        <p className="text-[10px] text-rose-300">{formatResolvedApiError(t, error)}</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5">
        {loading && displayUrls.length === 0 ? (
          <div className={`flex ${tileClass} items-center justify-center rounded-lg border border-white/[0.06] bg-[#0e0e16]`}>
            <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
          </div>
        ) : null}
        {!loading && displayUrls.length === 0 && !canEdit ? (
          <span className="text-[10px] text-amber-400/80">{t('shots.sceneAssetsMissing')}</span>
        ) : null}
        {displayUrls.map((url, index) => {
          const asset =
            assets.find((a) => assetMediaUrl(a) === url) ??
            assets.find((a) => resolveMediaDisplayUrl(assetMediaUrl(a)) === resolveMediaDisplayUrl(url)) ??
            null
          return (
            <div key={`${url}-${index}`} className={`relative shrink-0 overflow-hidden rounded-lg border border-white/[0.06] bg-[#12121a] ${tileClass}`}>
              <button
                type="button"
                className="h-full w-full"
                onClick={() =>
                  onPreview?.(
                    displayUrls,
                    index,
                    t('shots.sceneStoryboardPreview', { scene: sceneCode }),
                  )
                }
              >
                <img src={resolveMediaDisplayUrl(url)} alt="" className="h-full w-full object-cover" />
              </button>
              {canEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  disabled={busy}
                  className={appIconBtnClassName({
                    className:
                      'absolute -right-1 -top-1 h-5 w-5 rounded-full bg-zinc-900 p-0 text-zinc-400 ring-1 ring-white/10 hover:text-zinc-200',
                  })}
                  onClick={() => void handleRemove(asset, url)}
                >
                  <X className="h-3 w-3" />
                </Button>
              ) : null}
            </div>
          )
        })}
        {canEdit ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className={`flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-white/[0.08] bg-[#0e0e16] text-[9px] text-zinc-600 transition hover:border-indigo-500/30 hover:text-zinc-500 disabled:opacity-50 ${tileClass}`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {t('shots.sceneUploadRef')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setPickerOpen(true)}
              className={`flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-white/[0.08] bg-[#0e0e16] text-[9px] text-zinc-600 transition hover:border-indigo-500/30 hover:text-zinc-500 disabled:opacity-50 ${tileClass}`}
            >
              <Images className="h-3.5 w-3.5" />
              {t('shots.scenePickFromAssets')}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files ?? [])
                void (async () => {
                  for (const file of files) {
                    await handleUpload(file)
                  }
                })()
                e.target.value = ''
              }}
            />
          </>
        ) : null}
      </div>

      <AssetMultiPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        productionId={productionId}
        selectedUrls={displayUrls}
        onConfirm={(urls, catalog) => void handlePick(urls, catalog)}
      />
    </div>
  )
}
